import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Button, LinkButton} from '@components/common/base/buttons'
import {CompanySettingsPopup} from '@components/company-settings-popup/company-settings-popup'
import {ExtensionsGalleryPopup} from '@components/extensions-gallery/extensions-gallery'
import {useLoggedInStore} from '@components/logged-in/logged-in-store'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {UserAccess} from '@extensions/user-access'
import {Panel} from '../common/panel'
import {CompanyInvite} from '../company-invite/company-invite'
import {CompanyInvitePopup} from '../company-invite-popup/company-invite-popup'
import {EditableCompanyName} from './editable-company-name'

export const CompanySettings = ({
  className,
  extensionsPopupOpen,
  setExtensionsPopupOpen
}: {
  className?: string
  extensionsPopupOpen: boolean
  setExtensionsPopupOpen: (value: boolean) => void
}) => {
  return (
    <CompanySettingsBase
      className={className}
      extensionsPopupOpen={extensionsPopupOpen}
      setExtensionsPopupOpen={setExtensionsPopupOpen}
    />
  )
}

const TwTitle = tw.div`
  flex
  items-center
  gap-16
  h-32
`

const CompanySettingsBase = observer(
  ({
    className,
    extensionsPopupOpen,
    setExtensionsPopupOpen
  }: {
    className?: string
    extensionsPopupOpen: boolean
    setExtensionsPopupOpen: (value: boolean) => void
  }) => {
    const [settingsPopupOpen, setSettingsPopupOpen] = useState(false)
    const [invitePopupOpen, setInvitePopupOpen] = useState(false)
    const [loggedInStore] = useLoggedInStore()

    useEffect(() => {
      setExtensionsPopupOpen(loggedInStore.subpage === 'extensions')
    }, [loggedInStore.subpage])

    const canInvite = UserAccess.canInviteUser(globals.getCompanyId())

    return (
      <Panel
        title={
          <div className="flex gap-24">
            <EditableCompanyName />

            <LinkButton
              icon="IconCustomExtensionIcon"
              onClick={() => {
                setExtensionsPopupOpen(true)
                PageService.goToExtensions()
                void Tracking.send(TrackingEvent.myCompanyClickOnExtensions)
              }}
            >
              {Lang.msg.companies.extensions}
            </LinkButton>

            <LinkButton
              icon="IconSettings"
              onClick={() => {
                setSettingsPopupOpen(true)
              }}
            >
              {Lang.msg.companies.settings}
            </LinkButton>
          </div>
        }
        className={className}
        headerPaddings={'px-16 pt-16 pb-8'}
      >
        <TwTitle>
          {settingsPopupOpen && (
            <CompanySettingsPopup close={() => setSettingsPopupOpen(false)} />
          )}

          {extensionsPopupOpen && (
            <ExtensionsGalleryPopup
              onClose={() => {
                setExtensionsPopupOpen(false)
                PageService.goTo('projects')
              }}
            />
          )}

          {invitePopupOpen && (
            <CompanyInvitePopup
              close={() => setInvitePopupOpen(false)}
              inviteFrom={'dashboard'}
            />
          )}

          {canInvite && (
            <Button
              icon="IconCustomUsersAdd"
              size="small"
              onClick={() => {
                setInvitePopupOpen(true)
                void Tracking.send(
                  TrackingEvent.myCompanyClickOnInvitationByEmailButton
                )
              }}
            >
              {Lang.msg.companies.inviteByMail}
            </Button>
          )}

          <CompanyInvite />
        </TwTitle>
      </Panel>
    )
  }
)
